import React, { useState } from 'react';
import {
    X,
    Circle,
    Pause,
    Trash,
    TerminalWindow,
    EyeSlash,
    SpinnerGap,
    Cpu,
    ArrowsCounterClockwiseIcon,
    SpinnerGapIcon,
} from '@phosphor-icons/react';
import { BotStatusData, BotLogData } from '../function/type';
import { BotLogViewer } from './BotLogView';
import { deleteDockerContainer, restartDockerContainer, stopDockerContainer } from '../function/api';
import { useAppNotification } from 'hooks/useNotification';

interface BotStatusModalProps {
    isOpen: boolean;
    onClose: () => void;
    data: BotStatusData;
    onFetchLogs?: (account: string) => Promise<BotLogData>;
}

export const BotStatusModal: React.FC<BotStatusModalProps> = ({
    isOpen,
    onClose,
    data,
    onFetchLogs,
}) => {
    const [showLogs, setShowLogs] = useState(false);
    const [logContent, setLogContent] = useState<string>('');
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const notification = useAppNotification();

    if (!isOpen || !data) return null;

    const { container, status } = data;
    const stateLower = status.State ? status.State.toLowerCase() : '';
    const isRunning = stateLower === 'running';

    // Hàm xác định Style cho Badge trạng thái chuẩn xác theo Docker State
    const getStatusBadgeStyle = (state: string) => {
        switch (state) {
            case 'running':
                return {
                    badge: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
                    dot: 'text-emerald-500',
                };
            case 'paused':
            case 'restarting':
                return {
                    badge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
                    dot: 'text-amber-500',
                };
            default: // exited, dead, created, stopped
                return {
                    badge: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
                    dot: 'text-rose-500',
                };
        }
    };

    const statusStyle = getStatusBadgeStyle(stateLower);

    const handleToggleLogs = async () => {
        if (!showLogs) {
            if (onFetchLogs) {
                setLoadingLogs(true);
                try {
                    const res = await onFetchLogs(container.account);
                    setLogContent(res.logs.content);
                } catch (err) {
                    setLogContent('Lỗi khi lấy log từ máy chủ.');
                } finally {
                    setLoadingLogs(false);
                }
            }
            setShowLogs(true);
        } else {
            setShowLogs(false);
        }
    };

    const handleAction = async (
        actionName: string,
        actionFn?: (name: string) => Promise<void>
    ) => {
        if (!actionFn) return;
        setActionLoading(actionName);
        try {
            await actionFn(container.container_name);
        } catch (err) {
            console.error(`Lỗi thực hiện ${actionName}:`, err);
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            {/* Modal Card */}
            <div
                style={{ maxHeight: '90vh', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-700/20 transform transition-all dark:bg-slate-900 dark:border-slate-800 dark:text-white">

                {/* Header với Theme Tối đồng bộ */}
                <div className="bg-[#0b1928] px-7 py-5 relative text-white flex items-center justify-between border-b border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-slate-800/80 text-[#00be8a] border border-slate-700/50">
                            <Cpu size={24} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2.5">
                                <h3 className="text-lg font-bold text-white tracking-tight">
                                    {container.container_name}
                                </h3>
                                {/* Status Badge */}
                                <span
                                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold border ${statusStyle.badge}`}
                                >
                                    <Circle size={7} weight="fill" className={statusStyle.dot} />
                                    {status.State.toUpperCase()}
                                </span>
                            </div>
                            <p className="text-[12.5px] text-[#8ca0ba] mt-0.5">
                                ID: <span className="font-mono text-slate-300">{status.ID?.substring(0, 12)}</span> | Tạo lúc: {status.CreatedAt}
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white transition-colors duration-200 p-1"
                        aria-label="Đóng modal"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-7 !bg-[#0b1b2d] dark:bg-slate-900/30 text-slate-200 space-y-4">
                    {/* Thông tin Bot & Container */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-sm">
                        <div className="rounded-xl bg-[#f8fafc] dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/50 p-3.5">
                            <span className="text-[#8ca0ba] block text-[12px] font-semibold uppercase tracking-wider mb-0.5">Tài khoản</span>
                            <span className="font-semibold text-slate-800 dark:text-slate-100">{container.account}</span>
                        </div>

                        <div className="rounded-xl bg-[#f8fafc] dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/50 p-3.5">
                            <span className="text-[#8ca0ba] block text-[12px] font-semibold uppercase tracking-wider mb-0.5">Nền tảng</span>
                            <span className="font-bold text-[#00be8a] uppercase">{container.platform}</span>
                        </div>

                        <div className="rounded-xl bg-[#f8fafc] dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/50 p-3.5">
                            <span className="text-[#8ca0ba] block text-[12px] font-semibold uppercase tracking-wider mb-0.5">Server / User</span>
                            <span className="font-semibold text-slate-800 dark:text-slate-100">{container.server}</span>
                        </div>

                        <div className="rounded-xl bg-[#f8fafc] dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/50 p-3.5">
                            <span className="text-[#8ca0ba] block text-[12px] font-semibold uppercase tracking-wider mb-0.5">Thời gian chạy</span>
                            <span className="font-semibold text-slate-800 dark:text-slate-100">{status.Status}</span>
                        </div>
                    </div>

                    {/* Thông số Kỹ thuật */}
                    <div className="mt-4 rounded-xl bg-[#f8fafc] dark:bg-slate-800/30 border border-slate-200/80 dark:border-slate-800 p-3.5 text-xs space-y-1.5 text-slate-600 dark:text-slate-300">
                        <div>
                            <strong className="text-slate-700 dark:text-slate-200 font-semibold">Image:</strong> {status.Image}
                        </div>
                        <div>
                            <strong className="text-slate-700 dark:text-slate-200 font-semibold">Command:</strong>{' '}
                            <code className="bg-slate-200/60 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono text-[11px] text-slate-800 dark:text-slate-200">
                                {status.Command}
                            </code>
                        </div>
                        <div>
                            <strong className="text-slate-700 dark:text-slate-200 font-semibold">Size:</strong> {status.Size}
                        </div>
                    </div>

                    {/* Khối xem Log */}
                    <BotLogViewer
                        isOpen={showLogs}
                        logsContent={logContent}
                        onClose={() => setShowLogs(false)}
                        onRefresh={handleToggleLogs}
                    />
                </div>

                {/* Footer / Hàng Nút Bấm Thao Tác */}
                <div className="bg-[#f8fafc] dark:bg-slate-900/80 px-7 py-4 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                        {/* Nút Dừng */}
                        <button
                            onClick={async () => {
                                const data = await stopDockerContainer(status.ID);
                                if (data.status === 'stopped') {
                                    notification.success( 'Bot đã được dừng thành công.');
                                    onClose();
                                }
                                else {
                                    notification.error('Lỗi', 'Có lỗi xảy ra khi dừng Bot.');
                                }
                            }}
                            disabled={!isRunning || actionLoading === 'stop'}
                            className="flex items-center gap-1.5 rounded-xl bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white px-4 py-2.5 text-[13.5px] font-semibold shadow-sm transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {actionLoading === 'stop' ? (
                                <SpinnerGap size={16} className="animate-spin" />
                            ) : (
                                <Pause size={16} weight="bold" />
                            )}
                            <span>{actionLoading === 'stop' ? 'Đang dừng...' : 'Dừng Bot'}</span>
                        </button>
                        <button
                            onClick={async () => {
                                const data = await restartDockerContainer(container.container_name);
                                if (data.status === 'restarted') {
                                    notification.success('Bot đã được khởi động lại thành công.');
                                    onClose();
                                }
                                else {
                                    notification.error('Lỗi', 'Có lỗi xảy ra khi khởi động lại Bot.');
                                }
                            }}
                            disabled={actionLoading === 'restart'}
                            className="flex items-center gap-1.5 rounded-xl bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white px-4 py-2.5 text-[13.5px] font-semibold shadow-sm transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {actionLoading === 'restart' ? (
                                <SpinnerGapIcon size={16} className="animate-spin" />
                            ) : (
                                <ArrowsCounterClockwiseIcon size={16} weight="bold" />
                            )}
                            <span>{actionLoading === 'restart' ? 'Đang khởi động lại...' : 'Khởi động lại Bot'}</span>
                        </button>

                        {/* Nút Hiện / Ẩn Log */}
                        <button
                            onClick={handleToggleLogs}
                            disabled={loadingLogs}
                            className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-[13.5px] font-semibold shadow-sm transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed border-[#00be8a]/40 bg-[#00be8a]/10 text-[#00be8a] hover:bg-[#00be8a]/20`}
                        >
                            {loadingLogs ? (
                                <SpinnerGap size={16} className="animate-spin" />
                            ) : showLogs ? (
                                <EyeSlash size={16} />
                            ) : (
                                <TerminalWindow size={16} />
                            )}
                            <span>{loadingLogs ? 'Đang tải...' : showLogs ? 'Ẩn Log' : 'Hiện Log'}</span>
                        </button>
                    </div>

                    {/* Nút Xóa Bot */}
                    <button
                        onClick={async () => {
                            const data = await deleteDockerContainer(
                                status.ID,
                                container.account,
                                container.platform
                            );
                            if (data.status === 'removed') {
                                notification.success('Bot đã được xóa thành công.');
                                onClose();
                            } else {
                                notification.error('Lỗi', 'Có lỗi xảy ra khi xóa Bot.');
                            }
                        }}
                        disabled={actionLoading === 'delete'}
                        className="flex items-center gap-1.5 rounded-xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white px-4 py-2.5 text-[13.5px] font-semibold shadow-sm transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {actionLoading === 'delete' ? (
                            <SpinnerGap size={16} className="animate-spin" />
                        ) : (
                            <Trash size={16} weight="bold" />
                        )}
                        <span>{actionLoading === 'delete' ? 'Đang xóa...' : 'Xóa Bot'}</span>
                    </button>
                </div>

            </div>
        </div>
    );
};